import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Category, Product } from 'types/Category';

type ItemType = Category | Product | { bookingSharing: 'parking' };
type DataType = Pick<Category, 'categories' | 'products'> & { bookingSharing: 'parking' | null };

const useServicesSearch = (
    data: DataType,
): {
    items: ItemType[];
    searchText: string;
    handleSearchTextChange(val: string): void;
} => {
    const [searchText, setSearchedText] = useState<string>('');
    const { t } = useTranslation();

    const items = useMemo((): ItemType[] => {
        if (searchText) {
            const searchProduct = (product: Product): boolean =>
                product.name?.toLocaleLowerCase().includes(searchText.toLocaleLowerCase());

            const searchCategory = (acc: DataType, category: Category): DataType => {
                if (category.name?.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())) {
                    acc.categories = [...acc.categories, category];
                }
                const categoriesResult = category.categories.reduce(searchCategory, {
                    categories: [],
                    products: [],
                    bookingSharing: null,
                });
                const productsResult = category.products.filter(searchProduct, []);
                return {
                    categories: [...acc.categories, ...categoriesResult.categories],
                    products: [...acc.products, ...categoriesResult.products, ...productsResult],
                    bookingSharing: null,
                };
            };

            const flattenedCategories = data?.categories
                .map((category) => category.categories)
                .flat()
                .reduce(searchCategory, { categories: [], products: [], bookingSharing: null }) ?? {
                categories: [],
                products: [],
                bookingSharing: null,
            };
            const firstCategoryProducts =
                data?.categories
                    .map((category) => category.products)
                    .flat()
                    .filter(searchProduct) ?? [];
            const firstProducts = data?.products.filter(searchProduct) ?? [];

            const arr: ItemType[] = [
                {
                    categories: [...flattenedCategories.categories],
                    products: [...firstCategoryProducts, ...firstProducts, ...flattenedCategories.products],
                    name: '',
                    pictures: [],
                    id: 0,
                },
            ];
            if (
                'parking'.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()) ||
                t('services:parking').toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
            ) {
                arr.push({
                    bookingSharing: 'parking',
                });
            }
            return arr;
        }
        const categories = data?.categories ?? [];
        const products = data?.products ?? [];
        return [...categories, ...products, ...(data.bookingSharing ? [{ bookingSharing: 'parking' as const }] : [])];
    }, [data?.categories, data?.products, searchText, t, data.bookingSharing]);

    return { items, searchText, handleSearchTextChange: setSearchedText };
};

export default useServicesSearch;
